(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-kalaboost-video-stories]';
  const CARD_SELECTOR = '[data-kb-vs-card]';
  const PLAY_SELECTOR = '[data-kb-vs-play]';
  const SLOT_SELECTOR = '[data-kb-vs-slot]';
  const MODAL_SELECTOR = '[data-kb-vs-modal]';
  const MODAL_MEDIA_SELECTOR = '[data-kb-vs-modal-media]';
  const MODAL_TITLE_SELECTOR = '[data-kb-vs-modal-title]';
  const MODAL_PRODUCT_SELECTOR = '[data-kb-vs-modal-product]';
  const MODAL_MUTE_SELECTOR = '[data-kb-vs-modal-mute]';
  const MODAL_SHARE_SELECTOR = '[data-kb-vs-modal-share]';
  const MODAL_CLOSE_SELECTOR = '[data-kb-vs-modal-close]';
  const PRODUCT_SELECTOR = '.kalaboost-video-stories__product';
  const OPEN_CLASS = 'kalaboost-vs-modal-open';

  const instances = new WeakMap();

  function setMutedIcons(button, isMuted) {
    if (!button) return;

    const mutedIcon = button.querySelector('.kalaboost-video-stories__icon--muted');
    const unmutedIcon = button.querySelector('.kalaboost-video-stories__icon--unmuted');
    if (mutedIcon) mutedIcon.classList.toggle('is-hidden', !isMuted);
    if (unmutedIcon) unmutedIcon.classList.toggle('is-hidden', isMuted);

    const muteLabel = button.dataset.muteLabel || '';
    const unmuteLabel = button.dataset.unmuteLabel || '';
    button.setAttribute('aria-label', isMuted ? unmuteLabel : muteLabel);
  }

  class KalaboostVideoStories {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector(MODAL_SELECTOR);
      this.modalHome = this.modal?.parentElement || root;
      this.modalMedia = root.querySelector(MODAL_MEDIA_SELECTOR);
      this.modalTitle = root.querySelector(MODAL_TITLE_SELECTOR);
      this.modalProduct = root.querySelector(MODAL_PRODUCT_SELECTOR);
      this.modalMute = root.querySelector(MODAL_MUTE_SELECTOR);
      this.activeCard = null;
      this.activeSlot = null;
      this.activeVideo = null;
      this.lastFocus = null;
      this.shareUrl = '';
      this.shareTitle = '';
      this.onRootClick = this.onRootClick.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onVolumeChange = this.onVolumeChange.bind(this);
    }

    init() {
      this.root.addEventListener('click', this.onRootClick);
      this.modal?.addEventListener('click', this.onModalClick);
      document.addEventListener('keydown', this.onKeyDown);
    }

    destroy() {
      this.closeModal({ restoreFocus: false });
      this.root.removeEventListener('click', this.onRootClick);
      this.modal?.removeEventListener('click', this.onModalClick);
      document.removeEventListener('keydown', this.onKeyDown);
    }

    isModalOpen() {
      return Boolean(this.modal && !this.modal.hasAttribute('hidden'));
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.isModalOpen()) {
        event.preventDefault();
        this.closeModal();
      }
    }

    onVolumeChange(event) {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement)) return;
      this.syncMuteUi(video.muted);
    }

    syncMuteUi(isMuted) {
      if (this.activeCard) this.activeCard.classList.toggle('is-muted', isMuted);
      if (this.modal) this.modal.classList.toggle('is-muted', isMuted);
      setMutedIcons(this.modalMute, isMuted);
    }

    openModal(card) {
      if (!this.modal || !this.modalMedia || !card) return;

      const slot = card.querySelector(SLOT_SELECTOR);
      const video = slot?.querySelector('video');
      if (!slot || !video) return;

      if (this.isModalOpen()) this.closeModal({ restoreFocus: false });

      this.lastFocus = document.activeElement;
      this.activeCard = card;
      this.activeSlot = slot;
      this.activeVideo = video;
      this.shareUrl = card.dataset.shareUrl || window.location.href;
      this.shareTitle = card.dataset.modalTitle || document.title;

      if (this.modalTitle) {
        const title = card.dataset.modalTitle || '';
        this.modalTitle.textContent = title;
        this.modalTitle.hidden = title.trim() === '';
      }

      if (this.modalProduct) {
        this.modalProduct.replaceChildren();
        const product = card.querySelector(PRODUCT_SELECTOR);
        if (product && product.tagName === 'A') {
          const clone = product.cloneNode(true);
          clone.classList.add('kalaboost-video-stories__product--modal');
          const chevron = clone.querySelector('.kalaboost-video-stories__product-chevron');
          if (chevron) {
            chevron.innerHTML =
              '<svg viewBox="0 0 16 16" width="14" height="14" focusable="false" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="M6 3.5 10.5 8 6 12.5"></path></svg>';
          }
          this.modalProduct.appendChild(clone);
        }
      }

      this.modalMedia.appendChild(video);
      video.addEventListener('volumechange', this.onVolumeChange);
      this.syncMuteUi(video.muted);

      // The product column is a sticky, stacked context: the modal has to live on
      // the body to paint above the header.
      document.body.appendChild(this.modal);

      this.modal.removeAttribute('hidden');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add(OPEN_CLASS);

      const closeButton = this.modal.querySelector(MODAL_CLOSE_SELECTOR);
      if (closeButton) closeButton.focus();

      video.play().catch(() => {});
    }

    closeModal({ restoreFocus = true } = {}) {
      if (!this.modal || !this.isModalOpen()) {
        this.activeCard = null;
        this.activeSlot = null;
        this.activeVideo = null;
        return;
      }

      const video = this.activeVideo;
      if (video) {
        video.pause();
        video.removeEventListener('volumechange', this.onVolumeChange);
        if (this.activeSlot) this.activeSlot.appendChild(video);
        if (this.activeCard) this.activeCard.classList.toggle('is-muted', video.muted);
      }

      if (this.modalProduct) this.modalProduct.replaceChildren();

      this.modal.setAttribute('hidden', '');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove(OPEN_CLASS);
      this.modalHome.appendChild(this.modal);

      const focusTarget = this.lastFocus;
      this.activeCard = null;
      this.activeSlot = null;
      this.activeVideo = null;
      this.lastFocus = null;
      this.shareUrl = '';
      this.shareTitle = '';

      if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus();
      }
    }

    toggleMute() {
      if (!this.activeVideo) return;
      this.activeVideo.muted = !this.activeVideo.muted;
      this.syncMuteUi(this.activeVideo.muted);
    }

    async share() {
      const url = this.shareUrl || window.location.href;
      const title = this.shareTitle || document.title;

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
        }
      }

      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(url);
        } catch (error) {
          // Ignore clipboard failures silently.
        }
      }
    }

    onRootClick(event) {
      const playButton = event.target.closest(PLAY_SELECTOR);
      if (!playButton) return;

      event.preventDefault();
      this.openModal(playButton.closest(CARD_SELECTOR));
    }

    onModalClick(event) {
      if (event.target.closest(MODAL_CLOSE_SELECTOR)) {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.target.closest(MODAL_MUTE_SELECTOR)) {
        event.preventDefault();
        this.toggleMute();
        return;
      }

      if (event.target.closest(MODAL_SHARE_SELECTOR)) {
        event.preventDefault();
        this.share();
      }
    }
  }

  function initRoot(root) {
    if (!root || instances.has(root)) return;

    const instance = new KalaboostVideoStories(root);
    instance.init();
    instances.set(root, instance);
  }

  function destroyRoot(root) {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(destroyRoot);
  });
})();
